import React, { useMemo, useState } from "react";
import { Poptip, Table, Typography } from "@optimizely/axiom";
import { AccessManagementUserFormIconButton } from "./AccessManagementUserFormIconButton";
import { UserGroup } from "../../../../domain/UserGroup";
import { ProductAttribute } from "../../../../domain/ProductAttribute";
import { Role } from "../../../../domain/Role";
import { IOrganizationProductInstance } from "../../../../domain/OrganizationProduct";
import {
    ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
    ADMINCENTER_PRODUCT_ID,
    ADMINISTRATOR,
    ALL_PAGES_SIZE,
    ALL_PROJECTS_NAME,
    AMBIGUOUS_PRODUCT_GROUP_ERROR_MESSAGE,
    ATTRIBUTE_ROLES,
    CUSTOM_GROUP_ACCESS_ACTION,
    EXP_PROJECT_OWNER_PERMISSION,
    EXP_ROLE_PERMISSION_MAPPING,
    GROUP_TYPES,
    SYSTEM_ROLE_TYPE
} from "../../../../constants/constants";
import { useUserContext } from "../../../../providers/UserProvider";
import { ProductPermission } from "../../../../domain/Permission.interface";
import { User } from "../../../../domain/User";
import { RoleSelector } from "../../RoleSelector/RoleSelector";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../../feature-flags/flags";
import { useProducts } from "../../../../hooks/useProducts/useProducts";
import {
    getEntitledProductRoles,
    getProductGroupResolutionErrorKey,
    resolveProductGroupForRole
} from "../../../../lib/access-helpers";
import { emitToast } from "../../../../lib/toaster-utils";
import { datadogRum } from "@datadog/browser-rum";
import { useRoles } from "../../../../hooks/useRoles/useRoles";
import styles from "./AccessManagementUserFormInstanceTableRow.module.scss";
import classnames from "classnames";

type ProductInstanceRole = { project?: ProductAttribute; group: UserGroup; role: Role };
export const AccessManagementUserFormInstanceTableRow = ({
    existingRoleIds = [],
    hideInstanceName = false,
    instance,
    groupRole,
    groupRoleIndex,
    productAvailability,
    onGroupChanged,
    topLevelDelete = false
}: {
    existingRoleIds?: string[];
    hideInstanceName?: boolean;
    instance: IOrganizationProductInstance;
    groupRole: ProductInstanceRole;
    groupRoleIndex: number;
    productAvailability: ProductPermission | undefined;
    onGroupChanged: (previousGroupId: string | undefined, updatedGroupId: string | undefined) => Promise<User | void>;
    topLevelDelete?: boolean;
}) => {
    const isExpRow = productAvailability?.productId === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID;
    const isAdminCenterRow = productAvailability?.productId === ADMINCENTER_PRODUCT_ID;
    const { organizationName, organizationId, canUserDoAction } = useUserContext();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [selectedRole, setSelectedRole] = useState<Role>(groupRole.role);

    const [selectedPermissionValue] = selectedRole.name.split("-") || [];
    const productInstanceAccess = productAvailability?.instanceAccess;
    const nonCustomEXPRoles = Object.keys(EXP_ROLE_PERMISSION_MAPPING);
    // add owner since it's not in the mapping(?)
    nonCustomEXPRoles.push(EXP_PROJECT_OWNER_PERMISSION);
    const isCustomEXPRole = groupRole.project && nonCustomEXPRoles.indexOf(groupRole.role.displayName) === -1;

    // filter the groups by `groupType="product"`
    const productGroupTypeAccess = productInstanceAccess
        ?.filter((pi) => pi.instance.id === instance.id)
        ?.flatMap((item) => item.availableRoles)
        ?.filter(({ group }) => group.groupType === GROUP_TYPES.PRODUCT);

    // pull out the roles from the filtered groups
    const availableProductGroupTypeRolesForProductInstance = productGroupTypeAccess?.map((i) => i.role);

    const { enabled: filterRolesPerEntitlements } = useFeatureFlag(Flags.FILTER_GROUPS_AND_ROLES_PER_ENTITLEMENTS);
    const [orgId] = productGroupTypeAccess?.map((access) => access.group?.organizationId) ?? [];
    const { products = [], instances = [] } = useProducts({ organizationId: orgId });
    let adjustedRoles = availableProductGroupTypeRolesForProductInstance;
    if (filterRolesPerEntitlements) {
        adjustedRoles = getEntitledProductRoles({
            products,
            instances,
            roles: availableProductGroupTypeRolesForProductInstance || []
        });
    }
    // Get all Admin Center roles so we can later filter the dropdown to only "ProductDefined" roleTypes
    const { roles: allAdminCenterRoles } = useRoles({
        organizationId,
        productId: ADMINCENTER_PRODUCT_ID,
        instanceId: instance?.id,
        size: ALL_PAGES_SIZE
    });
    const productDefinedAdminCenterRoles = allAdminCenterRoles?.filter((r) => r.roleType === SYSTEM_ROLE_TYPE);
    const productDefinedAdminCenterRoleIds = productDefinedAdminCenterRoles?.map((r) => r.id);

    const isInstanceGroup = groupRole.group.isInstanceGroup;

    if (isInstanceGroup) {
        adjustedRoles = adjustedRoles?.filter((r) => r.isInstanceRole);
    } else {
        adjustedRoles = adjustedRoles?.filter((r) => !r.isInstanceRole);
    }

    const uniqueRoles: Role[] | undefined = adjustedRoles
        ?.reduce((prev: Role[], curr: Role) => {
            if (!prev.find((role) => role.id === curr.id)) {
                prev.push(curr);
            }
            return prev;
        }, [])
        .filter((r) => {
            // EXP Instance Admin Role doesn't have any projects associated
            // So we need to account for that here
            if (isInstanceGroup && r.name === ADMINISTRATOR) {
                return !!r;
            }
            // if the group role is tied to a project
            // filter out any role that is not tied to the project
            if (groupRole.project) {
                const roleForProject = r.attributes?.find((attr) => attr.id === groupRole.project!.id);
                return !!roleForProject && nonCustomEXPRoles.indexOf(r.displayName) > -1;
            }
            // if it's an Admin Center role
            // filter out any role that is not roleType = "ProductDefined"
            if (productAvailability?.productId === ADMINCENTER_PRODUCT_ID) {
                return productDefinedAdminCenterRoleIds?.includes(r.id);
            }
            return !!r;
        });

    /*
    Per Discussion with Ben: instead of filtering by attribute,
    delimit the role name by "-" and the first part represents the role name.

    Below is previous implementation for role dropdown filter where we
        filter out and look for the role name in the "permissions" key attribute,
        per discussion, we will for now get this name from the role name using the auto-generated
        "-" delimiter: {roleName} - {instance name?} `- {exp projectid} (optional ie for exp)`
     */
    // const roleDropdownOptions: Role[] | undefined = productAvailability?.instanceAccess
    //     .filter((ia) => ia.instance.id === instance.id)
    //     .flatMap((item) => item.availableRoles)
    //     .filter((ar) => !groupRole.project || ar.project?.id === groupRole.project?.id)
    //     .flatMap((ar) => ar.role);

    const onEditClick = () => setIsEditing(true);

    const onSaveClick = async () => {
        setIsSaving(true);

        // Find the correct product group for the selected role from the current instance.
        // Role linking can leave several product groups offering the same role on the same
        // instance, so resolve to the native group rather than taking the first match, and change
        // nothing when the target is not unambiguous (EPTS-6364).
        const resolution = resolveProductGroupForRole({
            instance,
            instanceAccess: productAvailability?.instanceAccess,
            // Project-scoped rows must not resolve against a group that grants this role only on a
            // sibling project. Instance-level and non-Experimentation rows carry no project and are
            // left unscoped.
            projectId: groupRole.project?.id,
            role: selectedRole
        });

        if (resolution.status !== "resolved") {
            datadogRum.addError(new Error(getProductGroupResolutionErrorKey(resolution.status)), {
                organizationId,
                instanceId: instance.id,
                roleId: selectedRole.id,
                resolution: resolution.status,
                candidateGroupIds: resolution.candidateGroupIds,
                candidateCount: resolution.candidateGroupIds.length,
                source: "AccessManagementUserFormInstanceTableRow.onSaveClick"
            });
            emitToast({ message: AMBIGUOUS_PRODUCT_GROUP_ERROR_MESSAGE, type: "bad-news" });
            setIsSaving(false);
            return;
        }

        const result = await onGroupChanged(groupRole.group.id, resolution.match.group.id);

        setIsSaving(false);
        setIsEditing(false);

        return result;
    };

    const onDeleteClick = async () => {
        setIsDeleting(true);
        const result = await onGroupChanged(groupRole.group.id, undefined);

        setIsDeleting(false);
        setIsEditing(false);

        return result;
    };

    const canAccessProjectEdit = canUserDoAction({
        action: [ATTRIBUTE_ROLES.GROUPS.UPDATE, ATTRIBUTE_ROLES.GROUPS.MANAGE],
        context: { AttributeId: groupRole.project?.id }
    });

    const canAccessInstanceEdit = canUserDoAction({
        action: [ATTRIBUTE_ROLES.GROUPS.UPDATE, ATTRIBUTE_ROLES.GROUPS.MANAGE],
        context: { InstanceId: instance.id }
    });

    const canAccessProductEdit = canUserDoAction({
        action: [ATTRIBUTE_ROLES.GROUPS.UPDATE, ATTRIBUTE_ROLES.GROUPS.MANAGE],
        context: { ProductId: productAvailability?.productId }
    });

    const canEditGroups = canAccessProjectEdit || canAccessInstanceEdit || canAccessProductEdit;
    // Don't allow editing Admin Center roles when they are paired with another product's role
    // EXP Instance Roles Admin and Project Owner are coupled with Admin Role
    const isAdminRolePairedWithAnotherRole =
        (isInstanceGroup && !groupRole.role.isInstanceRole) ||
        // EXP Single Project Owner is coupled with an Admin Role
        (groupRole.group.instancePermissions.length > 1 && !groupRole.role.isInstanceRole && isAdminCenterRow);
    const editButtonDisabled =
        !canEditGroups ||
        groupRole.group.groupType !== GROUP_TYPES.PRODUCT ||
        isCustomEXPRole ||
        isAdminRolePairedWithAnotherRole;

    const editTooltipMessage = () => {
        if (!canEditGroups) {
            return <Typography type="xs">You do not have permission to edit this role.</Typography>;
        } else if (isAdminRolePairedWithAnotherRole) {
            return (
                <Typography type="xxs" tag="div">
                    <span style={{ color: "#707070" }}>
                        This role is paired with another product role and cannot be edited here, please modify the
                        paired product role or contact your administrator for assistance.
                    </span>
                </Typography>
            );
        } else if (isCustomEXPRole) {
            return (
                <Typography type="xxs" tag="div">
                    <span style={{ color: "#707070" }}>
                        Custom role access cannot be edited here, it is assigned through a group: {groupRole.group.name}
                        . Please contact your administer for assistance.
                    </span>
                </Typography>
            );
        } else if (groupRole.group.groupType === GROUP_TYPES.CUSTOM) {
            return (
                <Typography type="xxs" tag="div">
                    <span style={{ color: "#707070" }}>
                        This role is assigned through a <strong>group</strong> and cannot be edited here. Group Name:{" "}
                        {groupRole.group.name}
                    </span>
                </Typography>
            );
        } else if (groupRole.group.groupType === GROUP_TYPES.INTERNAL) {
            return (
                <Typography type="xxs" tag="div">
                    <span style={{ color: "#707070" }}>
                        This role is assigned through an <strong>internal group</strong> and cannot be edited here.
                        Group Name: {groupRole.group.name}
                    </span>
                </Typography>
            );
        }
    };

    const customGroupAccessRemovalHandler = () => {
        window.dispatchEvent(
            new CustomEvent(ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME, {
                detail: {
                    action: CUSTOM_GROUP_ACCESS_ACTION.REMOVE,
                    id: groupRole.group.id,
                    name: groupRole.group.name,
                    userGroup: groupRole.group
                }
            })
        );
    };

    const getExperimentationRowProjectName = useMemo(() => {
        return isInstanceGroup || groupRole.role.isInstanceRole || !groupRole?.project?.name
            ? ALL_PROJECTS_NAME
            : groupRole?.project?.name;
    }, [isInstanceGroup, groupRole]);

    const rowClasses = classnames(styles["access-management-user-form-instance-table-row"], {
        "border--top": !hideInstanceName,
        "no-border--bottom": hideInstanceName
    });
    return (
        <Table.TR key={groupRole.group.id} className={rowClasses}>
            <Table.TD width="175px">
                {!hideInstanceName &&
                    (instance.name === "Admin Center" ? organizationName : instance.nickname || instance.name)}
            </Table.TD>

            {isExpRow && <Table.TD width="125px">{getExperimentationRowProjectName}</Table.TD>}

            <Table.TD width="200px">
                {isEditing ? (
                    <RoleSelector
                        selectedRole={selectedRole}
                        preExistingRoleIds={existingRoleIds}
                        project={groupRole.project}
                        roles={uniqueRoles || []}
                        onRoleChanged={setSelectedRole}
                        isDisabled={isSaving || isDeleting || (uniqueRoles || []).length < 1}
                        hideLabel={true}
                        isInitialized
                    />
                ) : groupRole.project ? (
                    selectedRole.displayName ? (
                        selectedRole.displayName
                    ) : (
                        selectedPermissionValue
                    )
                ) : (
                    selectedRole.name
                )}

                {editTooltipMessage()}
            </Table.TD>
            <Table.TD className={styles["access-management-user-form-instance-table-row__edit-cell"]}>
                {!isEditing ? (
                    <div>
                        <Poptip
                            trigger={editButtonDisabled ? "mouseenter" : "manual"}
                            content="Cannot edit access"
                            isInline
                        >
                            <AccessManagementUserFormIconButton
                                disabled={editButtonDisabled}
                                icon="pen-to-square"
                                onClick={onEditClick}
                            />
                        </Poptip>
                        {topLevelDelete && groupRole.group.groupType === GROUP_TYPES.CUSTOM && (
                            <AccessManagementUserFormIconButton
                                title="Delete"
                                icon="trash-can"
                                loading={isDeleting}
                                onClick={customGroupAccessRemovalHandler}
                            />
                        )}
                    </div>
                ) : (
                    <>
                        <AccessManagementUserFormIconButton
                            title="Save"
                            disabled={isSaving || isDeleting || !selectedRole.id}
                            loading={isSaving}
                            icon="square-check"
                            className="push-half--right"
                            onClick={onSaveClick}
                        />
                        {(!topLevelDelete || groupRole.group.groupType === GROUP_TYPES.PRODUCT) && (
                            <AccessManagementUserFormIconButton
                                title="Delete"
                                icon="trash-can"
                                loading={isDeleting}
                                onClick={onDeleteClick}
                            />
                        )}
                    </>
                )}
            </Table.TD>
        </Table.TR>
    );
};

AccessManagementUserFormInstanceTableRow.displayName = "AccessManagementUserFormInstanceTableRow";
